import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { SERVER_URL } from "../config";
import { XMarkIcon, ArrowDownTrayIcon, EyeIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import SearchableDropdown from "./SearchableDropdown";
import QuotationDocumentPreview from "./QuotationDocumentPreview";
import { buildQuotationPDF, computeRowTotals, fmt } from "./PdfQuotation";

/* ─── shared style tokens ─── */
const INPUT_CLASS =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

const INPUT_READONLY =
  "w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-400 cursor-not-allowed select-none";

const FieldLabel = ({ children }) => (
  <label className="text-xs text-slate-500 font-medium">{children}</label>
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-2 mb-3 mt-1">
    <div
      className="w-1 h-4 rounded-full flex-shrink-0"
      style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }}
    />
    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 m-0">
      {children}
    </p>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════ */
export default function QuotationModal({ isOpen, onClose }) {
  /* ── customer / lead search ── */
  const [customerQuery, setCustomerQuery]       = useState("");
  const [leads, setLeads]                       = useState([]);
  const [filteredLeads, setFilteredLeads]       = useState([]);
  const [showDropdown, setShowDropdown]         = useState(false);
  const [selectedLead, setSelectedLead]         = useState(null);
  const dropRef = useRef(null);

  /* ── quotation meta ── */
  const [subject,       setSubject]       = useState("");
  const [employeePhone, setEmployeePhone] = useState("");

  /* ── items: category / product description / qty / rate / gst ── */
  const [items, setItems] = useState([
    { category: "", item: "", qty: 1, rate: "", gstPct: 18 },
  ]);

  /* ── terms & conditions — fully user-defined, no hardcoded defaults ── */
  const [termsList, setTermsList] = useState([""]);

  /* ── product / category master list ──
     fetchProductAndCategory.php returns products under key "service" for
     category (kept as-is on the backend) and a separate "categories" list. */
  const [allProducts, setAllProducts] = useState([]); // [{name, category, price, status}]

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get(`${SERVER_URL}/Rahul/fetchProductAndCategory.php`)
      .then((r) => {
        if (r.data?.success) {
          const list = (r.data.products || [])
            .map((p) => ({
              name: p.name,
              category: p.service,
              price: p.price,
              status: p.status,
            }))
            .filter((p) => p.name);
          setAllProducts(list);
        }
      })
      .catch(console.error);
  }, [isOpen]);

  const categoryOptions = useMemo(() => {
    const cats = allProducts
      .filter((p) => p.status === "ACTIVE")
      .map((p) => p.category)
      .filter(Boolean);
    return [...new Set(cats)];
  }, [allProducts]);

  const productsByCategory = useMemo(() => {
    const map = {};
    allProducts
      .filter((p) => p.status === "ACTIVE")
      .forEach((p) => {
        if (!p.category || !p.name) return;
        if (!map[p.category]) map[p.category] = [];
        if (!map[p.category].some((x) => x.name === p.name)) map[p.category].push(p);
      });
    return map;
  }, [allProducts]);

  const toOpts = (arr) => arr.map((v) => ({ label: String(v), value: v }));

  /* ── submission / preview ── */
  const [submitting,      setSubmitting]      = useState(false);
  const [submitted,       setSubmitted]       = useState(false);
  const [quotationNumber, setQuotationNumber] = useState("");
  const [showPreview,     setShowPreview]     = useState(false);
  const [submitMsg,       setSubmitMsg]       = useState("");

  /* ── employee from localStorage ── */
  const emp          = JSON.parse(localStorage.getItem("employee") || "{}");
  const employeeName = emp.employee_name || "";

  /* ── fetch all leads once on open ── */
  useEffect(() => {
    if (!isOpen) return;
    axios
      .post(`${SERVER_URL}/DataFetch/leadHistory.php`, {
        userName: emp.employee_name,
        userId:   emp.employee_id,
      })
      .then((r) => { if (r.data.status === "success") setLeads(r.data.leads || []); })
      .catch(console.error);
  }, [isOpen]);

  /* ── filter dropdown: search across all lead fields ── */
  useEffect(() => {
    if (!customerQuery.trim()) { setFilteredLeads([]); setShowDropdown(false); return; }
    const q = customerQuery.toLowerCase();
    setFilteredLeads(
      leads
        .filter((c) =>
          (c.companyName         || "").toLowerCase().includes(q) ||
          (c.contactPerson       || "").toLowerCase().includes(q) ||
          (c.mobileNumber        || "").toLowerCase().includes(q) ||
          (c.mailId              || "").toLowerCase().includes(q) ||
          (c.address             || "").toLowerCase().includes(q) ||
          (c.product             || "").toLowerCase().includes(q) ||
          (c.location            || "").toLowerCase().includes(q) ||
          (c.branch              || "").toLowerCase().includes(q) ||
          (c.referenceName       || "").toLowerCase().includes(q) ||
          (c.prospect            || "").toLowerCase().includes(q)
        )
        .slice(0, 10),
    );
    setShowDropdown(true);
  }, [customerQuery, leads]);

  /* ── when a lead is selected, pre-populate first row's category from lead's product ── */
  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setCustomerQuery(lead.companyName || lead.contactPerson || "");
    setShowDropdown(false);
    setItems([{ category: "", item: "", qty: 1, rate: "", gstPct: 18 }]);
  };

  /* ── close dropdown on outside click ── */
  useEffect(() => {
    const h = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── reset on close ── */
  const handleClose = () => {
    setCustomerQuery(""); setSelectedLead(null);
    setSubject(""); setEmployeePhone("");
    setItems([{ category: "", item: "", qty: 1, rate: "", gstPct: 18 }]);
    setTermsList([""]);
    setSubmitted(false); setShowPreview(false); setSubmitMsg(""); setQuotationNumber("");
    onClose();
  };

  if (!isOpen) return null;

  /* ── item helpers ── */
  const addItem    = () => setItems((p) => [...p, { category: "", item: "", qty: 1, rate: "", gstPct: 18 }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  /* Changing a row's category clears its product (since the product
     dropdown is filtered to that category) and its auto-filled rate. */
  const handleCategoryChange = (i, category) => {
    setItems((p) => p.map((r, idx) => (idx === i ? { ...r, category, item: "", rate: "" } : r)));
  };

  /* Picking a product auto-fills Rate from the product master (still
     editable afterwards). */
  const handleProductChange = (i, productName) => {
    setItems((p) => p.map((r, idx) => {
      if (idx !== i) return r;
      const match = (productsByCategory[r.category] || []).find((x) => x.name === productName);
      return {
        ...r,
        item: productName,
        rate: match?.price != null ? String(match.price) : r.rate,
      };
    }));
  };

  /* ── terms helpers ── */
  const addTerm    = () => setTermsList((p) => [...p, ""]);
  const removeTerm = (i) => setTermsList((p) => p.filter((_, idx) => idx !== i));
  const updateTerm = (i, v) => setTermsList((p) => p.map((t, idx) => (idx === i ? v : t)));

  /* ── computed rows for the in-form Summary table ── */
  const computedRows = items.map((r) => ({ ...r, ...computeRowTotals(r) }));
  const grandTotal = computedRows.reduce((s, r) => s + r.inclTax, 0);

  /* ── validation ── */
  const isValid = () => {
    if (!selectedLead) return false;
    if (!subject.trim()) return false;
    return items.every((r) =>
      r.category.trim() !== "" &&
      r.item.trim() !== "" &&
      parseFloat(r.qty) > 0 &&
      r.rate !== "" && parseFloat(r.rate) >= 0
    );
  };

  /* ── PDF / preview params (shared shape used by quotationPdf.js & QuotationDocumentPreview) ── */
  const docParams = (forEmail) => ({
    branchName: selectedLead?.branch || "",
    quotationNumber,
    customer: selectedLead,
    rows: items,
    subject,
    employeeName,
    employeePhone,
    terms: termsList,
    forEmail,
  });

  /* ── submit to DB ── */
  const handleSubmit = async () => {
    if (!isValid()) {
      setSubmitMsg("Please select a lead, fill in a subject, and complete all item fields.");
      return;
    }
    setSubmitting(true); setSubmitMsg("");
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/insertQuotation.php`, {
        customerName: selectedLead?.companyName,
        leadId:       selectedLead?.lead_id,
        subject,
        items: items.map((r) => ({
          category:        r.category,
          itemDescription: r.item,
          qty:              parseFloat(r.qty) || 0,
          rate:             parseFloat(r.rate) || 0,
          gst:              parseFloat(r.gstPct) || 0,
        })),
        terms: termsList.map((t) => t.trim()).filter(Boolean),
        created_by: employeeName || emp.employee_id,
      });
      if (res.data.status === "success") {
        setSubmitted(true);
        setQuotationNumber(res.data.quotation_number || "");
        setSubmitMsg(`Quotation ${res.data.quotation_number || ""} saved successfully!`);
      } else {
        setSubmitMsg(res.data.message || "Failed to save quotation.");
      }
    } catch {
      setSubmitMsg("Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── send email ── */
  const handleSendEmail = async () => {
    if (!selectedLead?.mailId) { setSubmitMsg("No email found for selected lead."); return; }
    setSubmitting(true); setSubmitMsg("");
    try {
      const pdfDataUri = await buildQuotationPDF(docParams(true));
      await axios.post(`${SERVER_URL}/Quotation/sendQuotation.php`, {
        toEmail:    selectedLead.mailId,
        toName:     selectedLead.companyName,
        pdfBase64:  pdfDataUri.split(",")[1],
        senderName: employeeName,
      });
      setSubmitMsg("Quotation emailed successfully!");
    } catch {
      setSubmitMsg("Failed to send email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════ */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(8px)" }}
    >
      <div
        className="fixed top-0 left-0 right-0 h-0.5 z-50"
        style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }}
      />

      <div
        className="w-[820px] max-h-[90vh] rounded-xl flex flex-col bg-white border border-slate-200"
        style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div
          className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white rounded-t-xl"
          style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.06)" }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
            <div>
              <h3 className="text-lg font-bold m-0 tracking-tight text-slate-800">Create Quotation</h3>
              <p className="text-xs text-slate-400 m-0 mt-0.5">
                Select a lead · generates PDF · saves to database · sends via email
              </p>
            </div>
          </div>
          <button type="button" onClick={handleClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5"
          style={{ background: "#f0f4f8", scrollbarWidth: "thin", scrollbarColor: "rgba(148,163,184,0.5) rgba(241,245,249,1)" }}
        >

          {/* ── Lead / Customer Search ── */}
          <SectionTitle>Select Lead</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>

            <div className="relative" ref={dropRef}>
              <FieldLabel>Search by Company, Contact, Mobile, Product, Address… *</FieldLabel>
              <input
                className={INPUT_CLASS + " mt-1"}
                placeholder="Type anything — company name, product, mobile number, location…"
                value={customerQuery}
                disabled={submitted}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomerQuery(val);
                  if (selectedLead && val !== (selectedLead.companyName || selectedLead.contactPerson || "")) {
                    setSelectedLead(null);
                  }
                }}
                onFocus={() => customerQuery && setShowDropdown(true)}
              />

              {showDropdown && filteredLeads.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                  {filteredLeads.map((lead, i) => (
                    <div key={i}
                      className="px-4 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                      onClick={() => handleSelectLead(lead)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-700">{lead.companyName || "—"}</span>
                        {lead.product && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 font-medium">
                            {lead.product}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {lead.contactPerson} · {lead.mobileNumber}
                        {lead.location ? ` · ${lead.location}` : ""}
                        {lead.branch ? ` · ${lead.branch}` : ""}
                      </div>
                      {lead.mailId && (
                        <div className="text-xs text-blue-400">{lead.mailId}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Selected lead card */}
              {selectedLead && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-bold text-blue-800 m-0">{selectedLead.companyName}</p>
                      <p className="text-xs text-slate-500 mt-1 m-0">
                        {selectedLead.contactPerson} · {selectedLead.mobileNumber}
                        {selectedLead.alternateMobileNumber ? ` · ${selectedLead.alternateMobileNumber}` : ""}
                      </p>
                      {selectedLead.address && (
                        <p className="text-xs text-slate-500 m-0">
                          {selectedLead.address}{selectedLead.pinCode ? ` – ${selectedLead.pinCode}` : ""}
                        </p>
                      )}
                      {selectedLead.mailId && (
                        <p className="text-xs text-blue-500 m-0">{selectedLead.mailId}</p>
                      )}
                    </div>
                    {selectedLead.branch && (
                      <div className="ml-3 flex-shrink-0">
                        <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold border border-blue-200">
                          🏢 {selectedLead.branch}
                        </span>
                      </div>
                    )}
                  </div>
                  {selectedLead.prospect && (
                    <div className="mt-2 pt-2 border-t border-blue-100">
                      <span className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Prospect: </span>
                      <span className="text-xs text-slate-600">{selectedLead.prospect}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Quotation Details ── */}
          <SectionTitle>Quotation Details</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div className="flex flex-col gap-1 sm:col-span-2">
                <FieldLabel>Subject *</FieldLabel>
                <input
                  className={INPUT_CLASS}
                  placeholder="e.g. Offer for – Tally Customization."
                  value={subject}
                  disabled={submitted}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1">
                <FieldLabel>Prepared By</FieldLabel>
                <input className={INPUT_READONLY} value={employeeName} readOnly />
              </div>

              <div className="flex flex-col gap-1">
                <FieldLabel>Your Contact Number</FieldLabel>
                <input
                  className={INPUT_CLASS}
                  placeholder="e.g. 98765-43210"
                  value={employeePhone}
                  disabled={submitted}
                  onChange={(e) => setEmployeePhone(e.target.value)}
                />
              </div>

              {submitted && quotationNumber && (
                <div className="flex flex-col gap-1 sm:col-span-2">
                  <FieldLabel>Quotation Number</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input className={INPUT_READONLY} value={quotationNumber} readOnly />
                    <span className="text-xs text-emerald-600 font-semibold whitespace-nowrap">✓ Saved</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Quotation Items ── */}
          <SectionTitle>Quotation Items</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>

            <div className="grid grid-cols-[1.2fr_1.6fr_0.6fr_0.9fr_0.6fr_1fr_auto] gap-2 mb-2 pb-2 border-b border-slate-100">
              {["Category", "Product Description", "Qty", "Rate (₹)", "GST %", "Amount (Incl. Tax)", ""].map((h, i) => (
                <span key={i} className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{h}</span>
              ))}
            </div>

            {computedRows.map((row, i) => (
              <div key={i} className="grid grid-cols-[1.2fr_1.6fr_0.6fr_0.9fr_0.6fr_1fr_auto] gap-2 mb-2 items-start">
                <SearchableDropdown
                  label=""
                  options={toOpts(categoryOptions)}
                  value={row.category}
                  placeholder="Category"
                  disabled={submitted}
                  onChange={(val) => handleCategoryChange(i, val)}
                />
                <SearchableDropdown
                  label=""
                  options={toOpts((productsByCategory[row.category] || []).map((p) => p.name))}
                  value={row.item}
                  placeholder={row.category ? "Product Description" : "Select a category first"}
                  disabled={submitted || !row.category}
                  onChange={(val) => handleProductChange(i, val)}
                />
                <input
                  className={INPUT_CLASS}
                  placeholder="1"
                  type="number"
                  min="0"
                  disabled={submitted}
                  value={row.qty}
                  onChange={(e) => updateItem(i, "qty", e.target.value)}
                />
                <input
                  className={INPUT_CLASS}
                  placeholder="0"
                  type="number"
                  min="0"
                  disabled={submitted}
                  value={row.rate}
                  onChange={(e) => updateItem(i, "rate", e.target.value)}
                />
                <input
                  className={INPUT_CLASS}
                  placeholder="18"
                  type="number"
                  min="0"
                  disabled={submitted}
                  value={row.gstPct}
                  onChange={(e) => updateItem(i, "gstPct", e.target.value)}
                />
                <input className={INPUT_READONLY} value={`₹${fmt(row.inclTax)}`} readOnly />
                {items.length > 1 && !submitted
                  ? <button type="button" onClick={() => removeItem(i)}
                      className="p-2 rounded-lg text-red-400 border border-red-100 hover:bg-red-50 transition-colors">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  : <div className="w-8" />
                }
              </div>
            ))}

            {!submitted && (
              <button type="button" onClick={addItem}
                className="mt-1 text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                + Add Item
              </button>
            )}
          </div>

          {/* ── Terms & Conditions (fully user-defined) ── */}
          <SectionTitle>Terms &amp; Conditions</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            {termsList.map((term, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-400 w-4 flex-shrink-0">{i + 1}.</span>
                <input
                  className={INPUT_CLASS}
                  placeholder="e.g. GST @ 18% will be applicable on all prices mentioned above."
                  value={term}
                  disabled={submitted}
                  onChange={(e) => updateTerm(i, e.target.value)}
                />
                {termsList.length > 1 && !submitted && (
                  <button type="button" onClick={() => removeTerm(i)}
                    className="p-2 rounded-lg text-red-400 border border-red-100 hover:bg-red-50 transition-colors flex-shrink-0">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!submitted && (
              <button type="button" onClick={addTerm}
                className="mt-1 flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors">
                <PlusIcon className="h-3.5 w-3.5" /> Add Term
              </button>
            )}
          </div>

          {/* ── Summary ── */}
          <SectionTitle>Summary</SectionTitle>
          <div className="rounded-xl bg-white border border-slate-200 overflow-hidden mb-4"
            style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Category", "Item", "Qty", "Rate (₹)", "GST %", "Amount Incl. Tax (₹)"].map((h) => (
                    <th key={h} className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 px-4 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {computedRows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600">{r.category || "—"}</td>
                    <td className="px-4 py-2 text-slate-700">{r.item || "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{fmt(r.qty)}</td>
                    <td className="px-4 py-2 text-slate-600">₹{fmt(r.rate)}</td>
                    <td className="px-4 py-2 text-slate-500">{fmt(r.gst)}%</td>
                    <td className="px-4 py-2 font-semibold text-blue-700">₹{fmt(r.inclTax)}</td>
                  </tr>
                ))}
                <tr className="bg-blue-50">
                  <td colSpan={5} className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Grand Total</td>
                  <td className="px-4 py-3 font-bold text-blue-800 text-base">₹{fmt(grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white rounded-b-xl flex items-center gap-3 flex-wrap"
          style={{ boxShadow: "0 -1px 4px rgba(15,23,42,0.04)" }}>
          {submitMsg && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${
              submitMsg.toLowerCase().includes("success") || submitMsg.toLowerCase().includes("saved") || submitMsg.toLowerCase().includes("emailed")
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-red-50 text-red-600 border border-red-200"
            }`}>
              {submitMsg}
            </span>
          )}

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button type="button" onClick={handleClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 bg-white transition-colors">
              Cancel
            </button>

            {!submitted && (
              <button type="button" onClick={handleSubmit}
                disabled={submitting || !isValid()}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}>
                {submitting ? "Saving…" : "Submit Quotation"}
              </button>
            )}

            {submitted && (
              <>
                <button type="button" onClick={() => setShowPreview(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors">
                  <EyeIcon className="h-4 w-4" /> Preview
                </button>
                <button type="button" onClick={() => buildQuotationPDF(docParams(false))}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                  <ArrowDownTrayIcon className="h-4 w-4" /> Download PDF
                </button>
                <button type="button" onClick={handleSendEmail} disabled={submitting}
                  className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #0369a1, #0e7490)" }}>
                  {submitting ? "Sending…" : "Send to Email"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ══ Preview Overlay — same document component LeadTable uses ══ */}
      {showPreview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)" }}>
          <div className="w-[680px] max-h-[88vh] rounded-xl flex flex-col bg-white border border-slate-200"
            style={{ boxShadow: "0 24px 80px rgba(15,23,42,0.28)" }}>

            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <div>
                  <h3 className="text-base font-bold m-0 text-slate-800">Quotation Preview</h3>
                  {quotationNumber && (
                    <p className="text-xs text-blue-500 m-0 mt-0.5">#{quotationNumber}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => buildQuotationPDF(docParams(false))}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors">
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Download
                </button>
                <button type="button" onClick={() => setShowPreview(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6" style={{ background: "#f0f4f8" }}>
              <QuotationDocumentPreview {...docParams(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}